import { PaymentControlFormContextProvider } from '@ctx/paymentControlForm/provider';
import { usePaymentControlFormContext } from '@hooks/usePaymentControlsContext';
import { PaymentControlShow } from '@pages/paymentControls/id';
import { useDeletePaymentControl } from '@services/acquirer/merchant/paymentControls/delete';
import { useGetPaymentControl } from '@services/acquirer/merchant/paymentControls/get';
import { useUpdatePaymentControl } from '@services/acquirer/merchant/paymentControls/update';
import { createFileRoute, redirect, useNavigate, useParams } from '@tanstack/react-router';
import { PaymentControlsTypes } from '@utils/consts';
import { Loader } from '@components/loader';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

function MerchantPaymentControl() {
  const navigate = useNavigate();
  const { t } = useTranslation('app');
  const { localId, merchantId, type, id } = useParams({
    from: '/u/$localId/_acquirer/acquirer/merchants/$merchantId/payment-controls/$type/$id',
  });
  const { data, loading } = useGetPaymentControl({
    type: type as PaymentControlsTypes,
    merchantId,
    id,
  });
  const { formOpen, setFormOpen, formType } = usePaymentControlFormContext();

  const editRequestHandler = useUpdatePaymentControl({
    id: id,
    merchantId,
    type: formType,
    callback: () => {
      toast.success(t('toast.paymentControls.updateSuccess'));
      setFormOpen({ ...formOpen, edit: false });
    },
  });

  const { mutate, isPending } = useDeletePaymentControl({
    type: type as PaymentControlsTypes,
    merchantId,
    id,
    callback: () => {
      toast.success(t('toast.paymentControls.deleteSuccess'));
      navigate({
        to: '/u/$localId/acquirer/merchants/$merchantId/payment-controls/$type',
        params: { type, merchantId, localId },
      }).catch((err) => {
        console.error(err);
      });
    },
  });

  if (loading) return <Loader />;
  if (!data) return null;

  return (
    <PaymentControlShow
      paymentControl={data}
      editRequestHandler={editRequestHandler}
      deleteRequestHandler={{ onDelete: mutate, isPending }}
    />
  );
}

function PaymentControlWithFormProvider() {
  const { type } = useParams({
    from: '/u/$localId/_acquirer/acquirer/merchants/$merchantId/payment-controls/$type/$id',
  });

  return (
    <PaymentControlFormContextProvider type={type as PaymentControlsTypes}>
      <MerchantPaymentControl />,
    </PaymentControlFormContextProvider>
  );
}

export const Route = createFileRoute(
  '/u/$localId/_acquirer/acquirer/merchants/$merchantId/payment-controls/$type/$id',
)({
  beforeLoad: ({ context }) => {
    if (!context.userHasPermissions(['payment_controls_read'])) {
      throw redirect({
        to: `/u/$localId/${context.userType}/forbidden`,
      });
    }
  },
  component: PaymentControlWithFormProvider,
});
